<script setup>
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import { useSourceActions } from '../useSourceActions';
import { formatDateTime } from '../helpers';

const props = defineProps({
  conversationId: { type: Number, required: true },
  message: { type: Object, required: true },
  // { published_at, edited_at, author } from the source (RocketData reviews)
  replyMeta: { type: Object, default: () => ({}) },
});

const { t, locale } = useI18n();
const { isSubmitting, editReply } = useSourceActions();

const isEditing = ref(false);
const draft = ref('');
const textareaRef = ref(null);

const author = computed(
  () =>
    props.replyMeta.author ||
    props.message.sender?.available_name ||
    props.message.sender?.name ||
    ''
);

const publishedAt = computed(() => {
  if (props.replyMeta.published_at) return props.replyMeta.published_at;
  return props.message.created_at ? props.message.created_at * 1000 : null;
});

const metaLine = computed(() => {
  const parts = [author.value, formatDateTime(publishedAt.value, locale.value)];
  if (props.replyMeta.edited_at) {
    parts.push(
      t('MULTICHAT.SOURCE.REPLY.EDITED_AT', {
        date: formatDateTime(props.replyMeta.edited_at, locale.value),
      })
    );
  }
  return parts.filter(Boolean).join(' · ');
});

const startEditing = async () => {
  draft.value = props.message.content || '';
  isEditing.value = true;
  await nextTick();
  textareaRef.value?.focus();
};

const save = async () => {
  const content = draft.value.trim();
  if (!content) return;
  try {
    await editReply(props.conversationId, props.message, content);
    useAlert(t('MULTICHAT.SOURCE.REPLY.SAVED'));
    isEditing.value = false;
  } catch (error) {
    useAlert(t('MULTICHAT.SOURCE.REPLY.ERROR'));
  }
};

defineExpose({ startEditing });
</script>

<template>
  <div
    class="flex flex-col gap-2 p-3 rounded-lg bg-n-teal-2 border border-n-teal-4"
  >
    <div class="flex items-center gap-2 min-w-0">
      <span class="i-lucide-badge-check size-4 text-n-teal-11 flex-shrink-0" />
      <span class="text-xs font-semibold text-n-teal-11">
        {{ t('MULTICHAT.SOURCE.REPLY.PUBLISHED') }}
      </span>
      <span class="text-[11px] text-n-slate-10 truncate">{{ metaLine }}</span>
      <span class="flex-1" />
      <Button
        v-if="!isEditing"
        :label="t('MULTICHAT.SOURCE.REPLY.EDIT')"
        icon="i-lucide-pencil"
        size="xs"
        variant="faded"
        color="slate"
        @click="startEditing"
      />
    </div>
    <p
      v-if="!isEditing"
      class="m-0 text-[13px] leading-5 text-n-slate-12 whitespace-pre-line line-clamp-3"
    >
      {{ message.content }}
    </p>
    <template v-else>
      <textarea
        ref="textareaRef"
        v-model="draft"
        rows="4"
        class="w-full !mb-0 px-3 py-2 text-[13px] leading-5 rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 resize-y focus:outline-none focus:ring-1 focus:ring-n-brand"
        @keydown.meta.enter.prevent="save"
        @keydown.ctrl.enter.prevent="save"
      />
      <div class="flex items-center justify-end gap-2">
        <span class="text-[11px] text-n-slate-10 ltr:mr-auto rtl:ml-auto">
          {{ t('MULTICHAT.SOURCE.REPLY.HINT') }}
        </span>
        <Button
          :label="t('MULTICHAT.SOURCE.REPLY.CANCEL')"
          size="xs"
          variant="ghost"
          color="slate"
          @click="isEditing = false"
        />
        <Button
          :label="t('MULTICHAT.SOURCE.REPLY.SAVE')"
          size="xs"
          :is-loading="isSubmitting"
          :disabled="!draft.trim() || isSubmitting"
          @click="save"
        />
      </div>
    </template>
  </div>
</template>
